import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Checkbox,
  Select,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { listNotes, createNote, deleteNote, getNote, updateNoteExpiry } from '../api';

const EXPIRY_OPTIONS = [
  { value: '1month', label: '1 month' },
  { value: '3months', label: '3 months' },
  { value: '1year', label: '1 year' },
  { value: 'never', label: 'Never' },
];

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return 'Never';
  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires - now;
  if (diffMs < 0) return 'Expired';
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 1) return 'in 1 day';
  if (days < 30) return `in ${days} days`;
  const months = Math.round(days / 30);
  if (months <= 1) return 'in 1 month';
  if (months < 12) return `in ${months} months`;
  const years = Math.round(days / 365);
  return years <= 1 ? 'in 1 year' : `in ${years} years`;
};

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextKey, setNextKey] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const fetchNotes = async (reset = false) => {
    setLoading(true);
    try {
      const data = await listNotes(20, reset ? null : nextKey);
      setNotes(reset ? data.notes : [...notes, ...data.notes]);
      setNextKey(data.nextKey);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes(true);
  }, []);

  const handleCreate = async () => {
    if (!newNoteContent.trim()) return;

    setCreating(true);
    try {
      await createNote(newNoteContent);
      setNewNoteContent('');
      setCreateDialogOpen(false);
      fetchNotes(true);
    } catch (err) {
      console.error('Failed to create note:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await deleteNote(id);
      setNotes(notes.filter((n) => n.id !== id));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const handleView = async (id) => {
    try {
      const note = await getNote(id);
      setSelectedNote(note);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch note:', err);
    }
  };

  const handleCopy = () => {
    if (selectedNote) {
      navigator.clipboard.writeText(selectedNote.content);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} note(s)?`)) return;
    try {
      await Promise.all([...selected].map((id) => deleteNote(id)));
      setNotes(notes.filter((n) => !selected.has(n.id)));
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to bulk delete notes:', err);
    }
  };

  const handleExpiryChange = async (id, expiry) => {
    try {
      await updateNoteExpiry(id, expiry);
      setNotes(notes.map((n) => {
        if (n.id !== id) return n;
        const expiresAt = expiry === 'never' ? null : (() => {
          const days = { '1month': 30, '3months': 90, '1year': 365 }[expiry];
          return new Date(Date.now() + days * 86400000).toISOString();
        })();
        return { ...n, expiresAt };
      }));
    } catch (err) {
      console.error('Failed to update expiry:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === notes.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(notes.map((n) => n.id)));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Notes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Button variant="outlined" color="error" onClick={handleBulkDelete}>
              Delete ({selected.size})
            </Button>
          )}
          <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
            New Note
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < notes.length}
                  checked={notes.length > 0 && selected.size === notes.length}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Preview</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notes.map((note) => (
              <TableRow key={note.id} hover selected={selected.has(note.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(note.id)}
                    onChange={() => toggleSelect(note.id)}
                  />
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', maxWidth: 400 }}
                  onClick={() => handleView(note.id)}
                >
                  {note.preview}
                  {note.preview?.length >= 50 && '...'}
                </TableCell>
                <TableCell>{formatDate(note.createdAt)}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value=""
                    displayEmpty
                    renderValue={() => formatExpiry(note.expiresAt)}
                    onChange={(e) => handleExpiryChange(note.id, e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    {EXPIRY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleView(note.id)} size="small">
                    <OpenInNewIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(note.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {notes.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No notes yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {nextKey && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={() => fetchNotes()}>Load More</Button>
        </Box>
      )}

      {/* Create Note Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>New Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            placeholder="Enter your note..."
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Note Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Note
          <IconButton onClick={handleCopy} sx={{ ml: 1 }}>
            <ContentCopyIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {selectedNote?.content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notes;
